import { Popover } from "@overmap-ai/blocks"
import * as RadixToggleGroup from "@radix-ui/react-toggle-group"
import { type ComponentProps, type ComponentPropsWithoutRef, memo, type ReactElement, useCallback } from "react"

import { colors } from "../../utils/colors"
import { ColorButton } from "../Buttons/ColorButton"

export type ColorPickerPopoverProps = ComponentPropsWithoutRef<typeof Popover.Root> &
	Pick<ComponentProps<typeof Popover.Trigger>, "disabled"> &
	ComponentPropsWithoutRef<typeof Popover.Content> &
	(
		| {
				required: false
				color: string | undefined
				onColorChange: (color: string | undefined) => void
				children: ReactElement
		  }
		| {
				required: true
				color: string
				onColorChange: (color: string) => void
				children: ReactElement
		  }
	)

export const ColorPickerPopover = memo<ColorPickerPopoverProps>((props) => {
	const {
		open,
		onOpenChange,
		defaultOpen,
		modal,
		children,
		size,
		accentColor,
		radius,
		required,
		color,
		onColorChange,
		disabled,
		...rest
	} = props

	return (
		<Popover.Root open={open} onOpenChange={onOpenChange} defaultOpen={defaultOpen} modal={modal}>
			<Popover.Trigger asChild disabled={disabled}>
				{children}
			</Popover.Trigger>
			<Popover.Content size={size} accentColor={accentColor} radius={radius} {...rest}>
				{required ? (
					<_ColorPickerRequired colors={colors} color={color} onColorChange={onColorChange} size={size} />
				) : (
					<_ColorPickerNotRequired colors={colors} color={color} onColorChange={onColorChange} size={size} />
				)}
			</Popover.Content>
		</Popover.Root>
	)
})

ColorPickerPopover.displayName = "ColorPicker"

interface _ColorPickerRequiredProps {
	colors: string[]
	color: string
	onColorChange: (color: string) => void
	size: ComponentProps<typeof Popover.Content>["size"]
}

const _ColorPickerRequired = memo<_ColorPickerRequiredProps>((props) => {
	const { colors, color, onColorChange, size } = props

	const handleValueChange = useCallback(
		(color: string) => {
			if (color === "") return
			onColorChange(color)
		},
		[onColorChange],
	)

	return (
		<RadixToggleGroup.Root
			className="grid grid-cols-7 gap-1"
			type="single"
			value={color}
			onValueChange={handleValueChange}
		>
			{colors.map((color) => (
				<RadixToggleGroup.Item key={color} value={color} asChild>
					<ColorButton color={color} size={size} radius="full" />
				</RadixToggleGroup.Item>
			))}
		</RadixToggleGroup.Root>
	)
})
_ColorPickerRequired.displayName = "_ColorPickerRequired"

interface _ColorPickerNotRequiredProps {
	colors: string[]
	color: string | undefined
	onColorChange: (color: string | undefined) => void
	size: ComponentProps<typeof Popover.Content>["size"]
}

const _ColorPickerNotRequired = memo<_ColorPickerNotRequiredProps>((props) => {
	const { colors, color, onColorChange, size } = props

	const handleValueChange = useCallback(
		(color: string) => {
			onColorChange(color || undefined)
		},
		[onColorChange],
	)

	return (
		<RadixToggleGroup.Root
			className="grid grid-cols-7 gap-1"
			type="single"
			value={color}
			onValueChange={handleValueChange}
		>
			{colors.map((color) => (
				<RadixToggleGroup.Item key={color} value={color} asChild>
					<ColorButton color={color} size={size} radius="full" />
				</RadixToggleGroup.Item>
			))}
		</RadixToggleGroup.Root>
	)
})
_ColorPickerNotRequired.displayName = "_ColorPickerNotRequired"
